/**
 * free-gift-promo.js
 *
 * Reusable free-gift-with-qualifying-cart engine for InspiredGo.
 * MVP campaign: Peach Caprese (US-East) → unlock a free physical gift product.
 *
 * Architecture:
 *   - Wraps window.fetch to observe cart mutations without modifying existing theme code.
 *   - One FreeGiftPromo instance per [data-free-gift-promo] element on the page.
 *   - Auto-adds / auto-removes the gift line in response to cart-update events.
 *   - Renders a FREE overlay on the gift line via a CSS class applied through MutationObserver.
 *   - Sticky removal: once the shopper removes the gift, it does not re-add until the
 *     qualifying product leaves the cart and is re-added.
 */
(function () {
  'use strict';

  // Gift card variant IDs mirrored from assets/cart-flow.js — cart containing only these
  // must not trigger the promo (gift cards do not ship; the qualifying salad must be present).
  var GIFT_CARD_VARIANT_IDS = [31478736748586, 31478736781354, 31478736814122, 31478736846890];

  var STICKY_KEY_PREFIX = 'inspiredgo_promo_removed_';
  var DEBOUNCE_MS = 300;

  // -------------------------------------------------------------------------
  // Fetch wrapper — install once, before any cart fetches.
  // -------------------------------------------------------------------------
  if (!window.__inspiredgoFreeGiftFetchPatched) {
    window.__inspiredgoFreeGiftFetchPatched = true;
    var origFetch = window.fetch.bind(window);

    function dispatchCart(cart) {
      if (cart && typeof cart === 'object' && cart.items) {
        window.dispatchEvent(new CustomEvent('inspiredgo:cart:updated', { detail: cart }));
      }
    }

    function refetchAndDispatch() {
      origFetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(dispatchCart)
        .catch(function () {});
    }

    window.fetch = function (input, init) {
      var p = origFetch(input, init);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/cart(\.js|\/(add|change|update)\.js)/.test(url)) {
          p.then(function (res) {
            if (!res || !res.ok) return;
            try {
              res.clone().json().then(function (body) {
                if (body && typeof body === 'object' && body.items) {
                  // /cart.js, /cart/change.js, /cart/update.js → response is the cart.
                  dispatchCart(body);
                } else {
                  // /cart/add.js → response is the added line item, NOT the cart.
                  // Refetch /cart.js to get the canonical cart state.
                  refetchAndDispatch();
                }
              }).catch(function () {
                // Response wasn't JSON (e.g., theme requested ?sections=… markup).
                // Refetch the cart for canonical state.
                refetchAndDispatch();
              });
            } catch (_) {
              refetchAndDispatch();
            }
          }).catch(function () {});
        }
      } catch (_) {}
      return p;
    };
  }

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------
  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function parseIntList(raw) {
    if (!raw) return [];
    return String(raw).split(',').map(function (s) {
      var n = parseInt(String(s).trim(), 10);
      return isNaN(n) ? null : n;
    }).filter(function (n) { return n !== null; });
  }

  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function cartHasNonGiftCardItem(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return GIFT_CARD_VARIANT_IDS.indexOf(item.id) === -1;
    });
  }

  function cartHasQualifyingItem(cart, qualifyingVariantIds) {
    if (!cart || !cart.items || !qualifyingVariantIds.length) return false;
    return cart.items.some(function (item) {
      return qualifyingVariantIds.indexOf(item.id) !== -1
          || qualifyingVariantIds.indexOf(item.variant_id) !== -1
          || qualifyingVariantIds.indexOf(item.product_id) !== -1;
    });
  }

  function findGiftLine(cart, campaignId) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var props = cart.items[i].properties || {};
      if (String(props._promo_gift) === 'true' && String(props._promo_campaign) === String(campaignId)) {
        return { item: cart.items[i], index: i + 1 }; // Shopify line index is 1-based.
      }
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // FreeGiftPromo class — one per [data-free-gift-promo] element
  // -------------------------------------------------------------------------
  function FreeGiftPromo(root) {
    this.root = root;
    var raw = root.getAttribute('data-config') || '{}';
    try { this.config = JSON.parse(raw); } catch (e) { this.config = {}; }
    this.config.qualifying_variant_ids = (this.config.qualifying_variant_ids || [])
      .concat(parseIntList(root.getAttribute('data-qualifying-variant-ids')))
      .map(function (n) { return parseInt(n, 10); })
      .filter(function (n) { return !isNaN(n); });
    this.config.gift_variant_id = parseInt(this.config.gift_variant_id, 10);
    this.campaignId = String(this.config.campaign_id || 'default');
    this.customerBlocked = root.getAttribute('data-customer-blocked') === 'true';
    this.inFlight = false;
    this.lastCart = null;
    this.evaluate = debounce(this._evaluate.bind(this), DEBOUNCE_MS);
    this._onCartUpdated = function (e) { this.evaluate(e.detail); }.bind(this);
    window.addEventListener('inspiredgo:cart:updated', this._onCartUpdated);
  }

  FreeGiftPromo.prototype.stickyKey = function (cart) {
    var token = (cart && cart.token) || 'no-token';
    return STICKY_KEY_PREFIX + this.campaignId + '_' + token;
  };

  FreeGiftPromo.prototype.isStickyRemoved = function (cart) {
    try { return localStorage.getItem(this.stickyKey(cart)) === '1'; } catch (_) { return false; }
  };

  FreeGiftPromo.prototype.setStickyRemoved = function (cart) {
    try { localStorage.setItem(this.stickyKey(cart), '1'); } catch (_) {}
  };

  FreeGiftPromo.prototype.clearStickyRemoved = function (cart) {
    try { localStorage.removeItem(this.stickyKey(cart)); } catch (_) {}
  };

  FreeGiftPromo.prototype.broadcastState = function (state) {
    this.root.setAttribute('data-promo-state', state);
    window.dispatchEvent(new CustomEvent('inspiredgo:promo:state-change', {
      detail: { campaign_id: this.campaignId, state: state }
    }));
  };

  // Mark cart-sidebar rows for gift lines that carry our _promo_gift property
  // so the CSS can hide just the quantity control (.cs-item-qty). Idempotent:
  // only mutates when current marked set differs from desired, so it is safe
  // to call from a MutationObserver without causing a feedback loop.
  FreeGiftPromo.prototype._applyMarker = function () {
    if (!this.lastCart) return;
    var giftVariantId = this.config.gift_variant_id;
    var campaignId = this.campaignId;
    if (!giftVariantId) return;

    // Desired: lines with our _promo_gift+_promo_campaign properties.
    var hasMarkableLine = (this.lastCart.items || []).some(function (it) {
      var props = it.properties || {};
      return String(props._promo_gift) === 'true' && String(props._promo_campaign) === String(campaignId);
    });

    var desired = [];
    if (hasMarkableLine) {
      var selectors = [
        '[data-variant-id="' + giftVariantId + '"]',
        '[data-variant_id="' + giftVariantId + '"]',
        '[data-id="' + giftVariantId + '"]',
        '[data-key^="' + giftVariantId + ':"]',
        'input[name="updates[' + giftVariantId + ']"]'
      ];
      selectors.forEach(function (sel) {
        try {
          document.querySelectorAll(sel).forEach(function (el) {
            var row = el.closest('.cs-item, [data-cart-item], .cart-item, .cart__row, li.cart-item, tr.cart-item') || el;
            if (desired.indexOf(row) === -1) desired.push(row);
          });
        } catch (_) {}
      });
    }

    var current = Array.prototype.slice.call(document.querySelectorAll('.is-promo-gift-line'));

    // Remove marker from rows no longer in desired set.
    current.forEach(function (el) {
      if (desired.indexOf(el) === -1) el.classList.remove('is-promo-gift-line');
    });
    // Add marker to rows newly in desired set.
    desired.forEach(function (el) {
      if (!el.classList.contains('is-promo-gift-line')) el.classList.add('is-promo-gift-line');
    });
  };

  FreeGiftPromo.prototype.syncRegionFromCart = function (cart) {
    // Lovable bridge: if the cart was created via /cart/{variant}?attributes[region]=usa-east,
    // sync that region into locationDetector on landing.
    try {
      var attrRegion = cart && cart.attributes && cart.attributes.region;
      if (attrRegion && window.locationDetector && typeof window.locationDetector.setLocation === 'function') {
        // Convention: attributes[region]=usa-east → setLocation('US', 'USA-East')
        var v = String(attrRegion).toLowerCase();
        if (v === 'usa-east') window.locationDetector.setLocation('US', 'USA-East');
        else if (v === 'usa-west') window.locationDetector.setLocation('US', 'USA-West');
        else if (v === 'canada' || v === 'ca') window.locationDetector.setLocation('CA', null);
      }
    } catch (_) {}
  };

  FreeGiftPromo.prototype._evaluate = function (cart) {
    var self = this;
    var work = cart ? Promise.resolve(cart) : getCart();
    work.then(function (c) {
      if (!c || !c.items) return;
      self.syncRegionFromCart(c);

      var giftVariantId = self.config.gift_variant_id;
      var qualifyingIds = self.config.qualifying_variant_ids;

      if (!giftVariantId || !qualifyingIds.length) {
        // Section is misconfigured — fail closed, do nothing.
        self.broadcastState('idle');
        self.lastCart = c;
        return;
      }

      var giftLine = findGiftLine(c, self.campaignId);
      var qualifies = cartHasQualifyingItem(c, qualifyingIds);
      var notGiftCardOnly = cartHasNonGiftCardItem(c);

      if (window.__inspiredgoFreeGiftDebug) {
        console.log('[free-gift-promo] evaluate', {
          campaign: self.campaignId,
          qualifies: qualifies,
          notGiftCardOnly: notGiftCardOnly,
          giftPresent: !!giftLine,
          stickyRemoved: self.isStickyRemoved(c),
          customerBlocked: self.customerBlocked
        });
      }

      // Detect user-initiated removal: gift was present last tick, absent now, qualifying still present.
      var lastHadGift = self.lastCart && findGiftLine(self.lastCart, self.campaignId);
      if (lastHadGift && !giftLine && qualifies) {
        self.setStickyRemoved(c);
      }

      // Clear sticky once the qualifying product leaves the cart.
      if (!qualifies && self.isStickyRemoved(c)) {
        self.clearStickyRemoved(c);
      }

      // Decide.
      if (qualifies && self.customerBlocked && !giftLine) {
        // Customer has the re-claim block tag (e.g. handfuelpromoreceived) — do not add the gift.
        self.broadcastState('previously_claimed');
      } else if (qualifies && notGiftCardOnly && !giftLine && !self.isStickyRemoved(c) && !self.customerBlocked) {
        self._addGift().then(function () {
          self.broadcastState('claimed');
        }).catch(function (err) {
          if (window.console && console.warn) console.warn('[free-gift-promo] add failed', err);
          self.broadcastState('error');
        });
      } else if (!qualifies && giftLine) {
        self._removeGift(giftLine.index, c).then(function () {
          self.broadcastState('idle');
        }).catch(function (err) {
          if (window.console && console.warn) console.warn('[free-gift-promo] remove failed', err);
        });
      } else if (giftLine) {
        self.broadcastState('claimed');
      } else if (qualifies && !notGiftCardOnly) {
        // Cart contains only gift cards alongside the qualifying product → disqualified.
        self.broadcastState('disqualified');
      } else if (qualifies && self.isStickyRemoved(c)) {
        self.broadcastState('removed');
      } else {
        self.broadcastState('pre');
      }

      self.lastCart = c;
      self._applyMarker();
    }).catch(function (err) {
      if (window.console && console.warn) console.warn('[free-gift-promo] evaluate failed', err);
    });
  };

  // After an orchestrator-driven cart mutation, fetch the canonical cart and
  // notify cart-sidebar.js (and any other listener bound to the Shopify-native
  // 'cart:updated' event) so the UI re-renders to include / exclude the gift.
  function notifySidebarRefresh() {
    return getCart().then(function (cart) {
      try {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (_) {}
      return cart;
    }).catch(function () {});
  }

  // Global lock keyed by gift variant id — prevents multiple FreeGiftPromo
  // instances (banner + card on the same page) from racing to add the gift.
  window.__inspiredgoGiftAddLocks = window.__inspiredgoGiftAddLocks || {};

  FreeGiftPromo.prototype._addGift = function () {
    var self = this;
    var giftId = self.config.gift_variant_id;
    if (self.inFlight || window.__inspiredgoGiftAddLocks[giftId]) return Promise.resolve();
    self.inFlight = true;
    window.__inspiredgoGiftAddLocks[giftId] = true;

    function release() {
      self.inFlight = false;
      delete window.__inspiredgoGiftAddLocks[giftId];
    }

    // Re-fetch the canonical cart and check for the gift by VARIANT ID
    // (not by _promo_gift property). Recharge bundles, theme line rewrites,
    // and Shopify discount auto-adds may all create a line for our gift
    // variant without our properties — adding again would create a duplicate.
    return getCart().then(function (cart) {
      var alreadyPresent = (cart.items || []).some(function (it) {
        return it.id === giftId || it.variant_id === giftId;
      });
      if (alreadyPresent) {
        release();
        return null;
      }
      var body = {
        id: giftId,
        quantity: 1,
        properties: {
          _promo_gift: 'true',
          _promo_campaign: self.campaignId,
          _promo_added_at: new Date().toISOString()
        }
      };
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    }).then(function (r) {
      if (r === null) return; // already present, skipped
      release();
      if (!r.ok) throw new Error('add.js status ' + r.status);
      return r.json().then(function () { return notifySidebarRefresh(); });
    }).catch(function (e) {
      release();
      throw e;
    });
  };

  FreeGiftPromo.prototype._removeGift = function (lineIndex /*, cart */) {
    if (this.inFlight) return Promise.resolve();
    this.inFlight = true;
    var self = this;
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: 0 })
    }).then(function (r) {
      self.inFlight = false;
      if (!r.ok) throw new Error('change.js status ' + r.status);
      return r.json();
    }).then(function (result) {
      return notifySidebarRefresh().then(function () { return result; });
    }).catch(function (e) {
      self.inFlight = false;
      throw e;
    });
  };

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------
  function boot() {
    var roots = document.querySelectorAll('[data-free-gift-promo]');
    if (!roots.length) return;
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__freeGiftPromoInstance) return;
      var inst = new FreeGiftPromo(root);
      root.__freeGiftPromoInstance = inst;
      // Seed with current cart state.
      inst.evaluate();
    });
    // Re-apply the gift-line marker whenever the cart drawer / page re-renders.
    // The marker logic is idempotent (diffs current vs desired and only mutates
    // the difference), so this observer is loop-safe.
    if (!window.__inspiredgoGiftMarkerObserver) {
      window.__inspiredgoGiftMarkerObserver = new MutationObserver(function () {
        document.querySelectorAll('[data-free-gift-promo]').forEach(function (rootEl) {
          var inst = rootEl.__freeGiftPromoInstance;
          if (inst) inst._applyMarker();
        });
      });
      window.__inspiredgoGiftMarkerObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
